import type {
  Basket,
  Counterparty,
  Currency,
  Customer,
  CustomerConfiguration,
  CustomerQuote,
  ExecutionReport,
  ICustomerExecutionReport,
  MarketAccount,
  MarketAccountApiPermissionFilter,
  MarketAccountsContextProps,
  MarketsContextProps,
  Order,
  PriceProtectionEnum,
  ProductTypeEnum,
  Quote,
  SecuritiesContextProps,
  SecurityTypes,
  StrategiesContextProps,
  UnifiedLiquidityContextProps,
} from '@talos/kyoko';
import type { AdjustedSettlementDateResponse } from '../../containers/Blotters/CareOrders/adjustedSettlementDateApi';
import type { AppConfigState } from '../../providers/AppConfigProvider/AppConfigProvider.types';
import type { IFixingIndexContext } from '../../providers/FixingIndexProvider';
import type { IFundDefinitionContext } from '../../providers/FundDefinitionProvider';
import type { IOrderPreset } from '../../providers/OrderPresetsContext';
import type { MarketSelectorPreset } from '../MarketSelector/types';
import type { RFQCurrency } from './NewRFQ/types';
import type { CompleteViewLaunchDataMap, OMSView } from './OMSView';
import type { OMS_POPOUT_GROUP_VIEWS } from './viewsWithTabs';

export type MarketAccountReferenceData = Pick<
  MarketAccountsContextProps,
  'marketAccountsByMarket' | 'marketAccountsList' | 'marketAccountsByName'
> & { isReady: boolean };

export type SecuritiesReferenceData = Pick<
  SecuritiesContextProps,
  | 'securitiesList'
  | 'securitiesBySymbol'
  | 'futures'
  | 'futuresByCurrencyPair'
  | 'futuresByMarketByCurrency'
  | 'perps'
  | 'cfds'
  | 'perpsByCurrencyPair'
> & { isReady: boolean };

export type FixingIndicesReferenceData = Pick<
  IFixingIndexContext,
  'fixingIndicesList' | 'fixingIndicesByName' | 'tradableIndicesBySymbol' | 'marketsByFixingIndex'
> & { isReady: boolean };

export type FundDefinitionReferenceData = Pick<IFundDefinitionContext, 'fundsBySymbol' | 'fundsList'>;

export interface AdjustedSettlementDatesReferenceData {
  settlementDates: AdjustedSettlementDateResponse[];
}

export interface BasketReferenceData {
  basketsList: Basket[];
}

export type StrategiesReferenceData = Pick<
  StrategiesContextProps,
  'strategiesList' | 'strategiesByName' | 'ddhStrategiesList'
> & { isReady: boolean };

export interface MarketsReferenceData extends Pick<MarketsContextProps, 'marketsList' | 'marketsByName'> {
  isReady: boolean;
}

export type OptionRefData = {
  options: SecurityTypes['Option'][];
  expirationByMarketByCurrencyIdentity: Map<string, Map<string, Set<string>>>;
  optionSecurityBySymbol: Map<string, SecurityTypes['Option']>;
  strikesByOptionKey: Map<string, Map<string, string>>;
  isReady: boolean;
};

export type CurrenciesRefData = {
  currenciesBySymbol: Map<string, Currency>;
  isReady: boolean;
};

export type OrderPresetsRefData = {
  orderPresetsList: IOrderPreset[];
  orderPresetsByID: Map<string, IOrderPreset>;
  isReady: boolean;
};

export interface MarketAccountPermissionFiltersRefData {
  marketAccountFilterIDsBySubAccountName: Map<string, Set<string>>;
  marketAccountPermissionFiltersByFilterID: Map<string, MarketAccountApiPermissionFilter>;
  isReady: boolean;
}

export type UnifiedLiquidityTokenReferenceData = UnifiedLiquidityContextProps['unifiedLiquidityTokenBySymbol'];

// NOTE: we are not deep copying the objects
// Simply keeping reference to them which in turn share the underlying references for ease of use in other slices
export interface OMSReferenceDataState {
  defaultSubAccount?: string;
  customers: Customer[];
  customerMarketAccounts: MarketAccount[];
  customerConfigurations: CustomerConfiguration[];

  securities: SecuritiesReferenceData;
  strategies: StrategiesReferenceData;
  markets: MarketsReferenceData;
  marketAccounts: MarketAccountReferenceData;
  currencies: CurrenciesRefData;
  options: OptionRefData;
  orderPresets: OrderPresetsRefData;
  counterparties: Counterparty[];
  marketAccountPermissionFilters: MarketAccountPermissionFiltersRefData;

  settings: OMSSettingsState;
  unifiedLiquidityTokens: UnifiedLiquidityTokenReferenceData;
  fixingIndices: FixingIndicesReferenceData;
  baskets: BasketReferenceData;
  funds: FundDefinitionReferenceData;
  adjustedSettlementDates: AdjustedSettlementDatesReferenceData;

  tradingAggregationPresets: MarketSelectorPreset[] | undefined;
}

export type BasketSettleOffsetOverride = Record<string, { Sell?: number; Buy?: number }> | undefined;

type OMSFeatureFlags = {
  defaultPriceProtection: PriceProtectionEnum;
  supportedStrategiesForPricingModes: string[];
  enableOptionTrading: boolean;
  enableNegotiatedRFQFlow: boolean;
  enableTakerMarketplaceETFRFQFlow: boolean;
  enableCareOrderOrders: boolean;
  careOrderBlockDirectExecution: boolean;
  truncateCareOrderID: boolean;
  enableAccountSegregation: boolean;
  enableMSSValidation: boolean;
  marketSelectionAsyncRefDataTimeoutMs: number;
  disableNonSTFNegotiatedRFQ: boolean;
  showStFMarketsInNonStFNegotiatedRFQ: boolean;
  disableUnifiedLiquidityDerivativesMarketSelectionRestriction: boolean;
  enableUnifiedLiquidityMarketSymbols: boolean;
  disableCrossCurrencyQty: boolean;
  nonSpotDelta1DefaultQty: string;
  enableRFQFormFundSecurities: boolean;
  enableRFQFormOptions: boolean;
  enableRFQFormOTCs: boolean;
  enableSTSDigitalCustomOptionRFQ: boolean;
  enablePredictionMarkets: boolean;
  enableRFQSweeping: boolean;
  enableRFQSizeCounteringMultitokenCompound: boolean;
  enableRFQSizeCounteringSpot: boolean;
  rfqFormClearingAccountProductTypes: ProductTypeEnum[];
  sizeCounteringImpliesPartialFill: boolean;
  fundCareOrderDefaultSubAccounts: Record<string, string> | undefined;
  useFundDefinitionAndTradableIndexesStreams: boolean;
  supportAdjustedSettlementDate: boolean;
  defaultBasketSettleOffsetOverride: BasketSettleOffsetOverride;
  enableRFQFormFundField: boolean;
  enableDurationSubmission: boolean;
};

export const MARKET_SELECTION_ASYNC_REFDATA_TIMEOUT_MS = 1500;

export interface OMSSettingsState
  extends
    OMSFeatureFlags,
    Pick<
      AppConfigState,
      | 'lastProductGroupUsed'
      | 'lastStrategyUsed'
      | 'useTradeAllocations'
      | 'enableGroups'
      | 'enableDerivativeContractDefault'
      | 'defaultNewOrderToCounterCurrency'
      | 'alwaysCheckPriceReasonability'
      | 'defaultOrderStrategy'
      | 'defaultMultilegOrderStrategy'
      | 'showAllInPrices'
      | 'showDelta1BPS'
      | 'allowSyntheticCcy'
      | 'allowSyntheticCrosses'
      | 'exclusivelyPrimePriceOnRePriming'
      | 'enableReduceFirst'
      | 'enableReduceOnly'
      | 'ddhDefaultsByID'
      | 'defaultToEmptyOrderMarkets'
      | 'defaultMarketAccounts'
      | 'defaultAggregationId'
      | 'resetOrderAccountsOnSubAccountChange'
      | 'rfqFormTradingProtocol'
      | 'rfqFormDefaultToSweeping'
      | 'rfqFormDefaultToSizeCountering'
    > {}

export type OMSPopoutGroupType = keyof typeof OMS_POPOUT_GROUP_VIEWS;

export interface OMSPopoutGroup {
  name: OMSPopoutGroupType;
  views: OMSView[];
  activeView: OMSView;
  features: OMSPopoutFeatures;
  /** We need to keep a reference to the window object to move focus to popouts */
  _window?: Window;
}

export interface OMSPopoutFeatures {
  width: number;
  height: number;
  left: number;
  top: number;
  [name: string]: number | string | boolean | undefined;
}

export type FeedbackCardEntityType = 'Order' | 'Quote' | 'SplitRFQ';

/**
 * Feedback payload for the split RFQ post-submit overlay.
 *
 * `results` is the immediate per-market submission outcome. Successful rows carry
 * the first quote snapshot so the overlay can render right away; `sweepGroupID`
 * lets the feedback card subscribe to the full live quote set and hydrate those
 * successful rows as later quote updates arrive.
 */
export interface SplitRFQFeedbackEntity {
  type: 'splitRFQ';
  sweepGroupID: string;
  results: SplitRFQFeedbackResult[];
  currency: RFQCurrency | undefined;
}

/** One feedback row per submitted child RFQ. Failures are terminal submission outcomes. */
export type SplitRFQFeedbackResult =
  | {
      status: 'success';
      quote: Quote;
    }
  | {
      status: 'failed';
      marketAccount: string;
      reason: string;
    };

export type FeedbackCardPayload =
  | {
      entityType: 'Order';
      entity: Order | ExecutionReport | ICustomerExecutionReport | null;
    }
  | {
      entityType: 'Quote';
      entity: Quote | CustomerQuote;
    }
  | {
      entityType: 'SplitRFQ';
      entity: SplitRFQFeedbackEntity;
    };

export const isSplitRFQFeedbackPayload = (
  payload: FeedbackCardPayload | undefined
): payload is Extract<FeedbackCardPayload, { entityType: 'SplitRFQ' }> => {
  return payload?.entityType === 'SplitRFQ';
};

export interface OMSState {
  openedView?: OMSView;
  popoutGroups: Partial<Record<OMSPopoutGroupType, OMSPopoutGroup>>;
  placedEntity?: FeedbackCardPayload;
  openedViewLaunchData?: CompleteViewLaunchDataMap[OMSView] | undefined;
}

export type OMSContentProps = {
  isMobile?: boolean;
  view?: OMSView;
  onClose: () => void;
  onChangeTab?: (view: OMSView) => void;
} & (
  | {
      onPopout: (popoutGroupName: OMSPopoutGroupType) => void;
      isPopout?: false;
    }
  | {
      isPopout: true;
      onPopout?: undefined;
    }
);

export interface OMSFormProps {
  isPopout: boolean;
  hideProductGroupSelector?: boolean;
}
